import React, { useCallback, useContext, useMemo } from 'react';
import { Card, Table } from 'antd';
import { sortBy } from 'lodash';
import { useQuery } from 'react-query';
import {
  getBasketballTournamentStatistics,
  getCardsCount,
  getLeaders,
  getPositions,
  getVolleyballPositions,
} from 'api/events';
import { EventContext } from '.';
import {
  cardsTableColumns,
  leadersTableColumns,
  positionsTableColumns,
  throwsBasketballColumns,
  volleyballPositionsColumns,
} from 'configs/columns/stadisticsConfig';

const Stadistics = () => {
  const {
    event: { sport_name },
    eventId,
    groups,
  } = useContext(EventContext);

  const sortedGroups = useMemo(() => sortBy(groups, g => !g.play_off), [groups]);

  const positionsResolver = useCallback(async (key, _eventId) => {
    const { body } = await getPositions(_eventId);

    return body?.data?.position_tables || [];
  }, []);

  const positionsResolverVoleyball = useCallback(async (key, _eventId) => {
    const { body } = await getVolleyballPositions(_eventId);
    return (
      {
        positionsData: body?.data?.position_tables,
        best_of: body?.data?.tournament_configuration?.best_of,
      } || {}
    );
  }, []);

  const { data } = useQuery(
    ['event-positions', eventId],
    sport_name === 'Volleyball' ? positionsResolverVoleyball : positionsResolver,
  );

  let positionsData = [];
  let best_of = 0;

  if (sport_name === 'Volleyball') {
    positionsData = data?.positionsData;
    best_of = data?.best_of;
  } else {
    positionsData = data;
  }

  const leadersResolver = useCallback(async (key, _eventId) => {
    const { body } = await getLeaders(_eventId);
    return body?.data?.goal_leaders || [];
  }, []);

  const { data: leadersData } = useQuery(['event-leaders', eventId], leadersResolver);

  const cardsCountResolver = useCallback(async (key, _eventId) => {
    const { body } = await getCardsCount(_eventId);

    return body?.data?.cards_count || [];
  }, []);

  const { data: cardsCountData } = useQuery(['event-cards-count', eventId], cardsCountResolver);

  const freeThrowsResolver = useCallback(async (key, _eventId) => {
    const { body } = await getBasketballTournamentStatistics(_eventId, 'free_throw');
    return body?.data?.free_throws || [];
  }, []);

  const twoThrowsResolver = useCallback(async (key, _eventId) => {
    const { body } = await getBasketballTournamentStatistics(_eventId, 'two_points');
    return body?.data?.two_points || [];
  }, []);

  const threeThrowsResolver = useCallback(async (key, _eventId) => {
    const { body } = await getBasketballTournamentStatistics(_eventId, 'three_points');
    return body?.data?.three_points || [];
  }, []);

  const { data: freeThrowsData } = useQuery(
    ['event-freeThrows', eventId],
    sport_name === 'Basketball' ? freeThrowsResolver : null,
  );

  const { data: twoThrowsData } = useQuery(
    ['event-twoThrows', eventId],
    sport_name === 'Basketball' && twoThrowsResolver,
  );

  const { data: threeThrowsData } = useQuery(
    ['event-threeThrows', eventId],
    sport_name === 'Basketball' && threeThrowsResolver,
  );

  return (
    <>
      {sortedGroups.map(group => {
        const positions = positionsData ? positionsData[group?.id] || [] : [];
        positions.map((position, idx) => {
          position.rank = idx + 1;
          position.total_matches =
            position.won_matches + position.lost_matches + position.draw_matches;
          return position;
        });
        return (
          <Card
            key={group?.id}
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: '16px',
                }}
              >
                {group.name}
              </div>
            }
            bodyStyle={{ padding: '0px' }}
          >
            <Table
              columns={
                sport_name === 'Volleyball'
                  ? volleyballPositionsColumns(best_of)
                  : positionsTableColumns
              }
              dataSource={positions}
              bordered
              rowKey={obj => obj?.club?.id}
            />
          </Card>
        );
      })}
      {sport_name !== 'Volleyball' && sport_name !== 'Basketball' ? (
        <Card
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingBottom: '16px',
              }}
            >
              TABLA DE GOLEADORES
            </div>
          }
          bodyStyle={{ padding: '0px' }}
        >
          <Table
            columns={leadersTableColumns}
            dataSource={leadersData}
            rowKey={obj => Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}
          />
        </Card>
      ) : null}
      {sport_name !== 'Volleyball' && sport_name !== 'Basketball' ? (
        <Card
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingBottom: '16px',
              }}
            >
              TARJETAS
            </div>
          }
          bodyStyle={{ padding: '0px' }}
        >
          <Table
            columns={cardsTableColumns}
            dataSource={cardsCountData}
            rowKey={obj => Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}
          />
        </Card>
      ) : null}
      {sport_name === 'Basketball' && (
        <>
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: '16px',
                }}
              >
                TIROS LIBRES
              </div>
            }
            bodyStyle={{ padding: '0px' }}
          >
            <Table
              columns={throwsBasketballColumns}
              dataSource={freeThrowsData}
              rowKey={obj => Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}
            />
          </Card>
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: '16px',
                }}
              >
                DOBLES
              </div>
            }
            bodyStyle={{ padding: '0px' }}
          >
            <Table
              columns={throwsBasketballColumns}
              dataSource={twoThrowsData}
              rowKey={obj => Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}
            />
          </Card>
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: '16px',
                }}
              >
                TRIPLES
              </div>
            }
            bodyStyle={{ padding: '0px' }}
          >
            <Table
              columns={throwsBasketballColumns}
              dataSource={threeThrowsData}
              rowKey={obj => Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}
            />
          </Card>
        </>
      )}
    </>
  );
};

export default Stadistics;
